import { ref } from 'vue'
import { useToast } from 'vue-toastification'

export interface ErrorConfig {
  showToast?: boolean
  toastMessage?: string
  logError?: boolean
  rethrow?: boolean
}

export function useErrorHandling() {
  const toast = useToast()
  const errors = ref<Record<string, string | null>>({})

  const handleError = (
    error: any,
    context: string,
    config: ErrorConfig = {}
  ) => {
    const {
      showToast = true,
      toastMessage,
      logError = true,
      rethrow = false
    } = config

    // Extract error message
    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
    
    // Store error in state
    errors.value[context] = errorMessage

    // Log error if enabled
    if (logError) {
      console.error(`Error in ${context}:`, error)
    }

    // Show toast notification if enabled
    if (showToast) {
      const message = toastMessage || `Error in ${context}: ${errorMessage}`
      toast.error(message)
    }

    // Rethrow if needed
    if (rethrow) {
      throw error
    }
  }

  const clearError = (context: string) => {
    delete errors.value[context]
  }

  const clearAllErrors = () => {
    errors.value = {}
  }

  const getError = (context: string) => {
    return errors.value[context] || null
  }

  const hasError = (context: string) => {
    return !!errors.value[context]
  }

  return {
    errors,
    handleError,
    clearError,
    clearAllErrors,
    getError,
    hasError
  }
}